<!-- 
*****************************************************************************
**                                                                         **
** Copyright (c) 2012-2024, Tyker Technology BV                            **
** All rights reserved.                                                    **
**                                                                         **
** This software is the confidential and proprietary information of        **
** Tyker Technology BV. Unauthorized copying or disclosure of this file,   **
** via any medium is strictly prohibited. This file is only available to   **
** authorized individuals with the permission of the copyright holders.    **
** If you encounter this file and do not have permission, please contact   ** 
** the copyright holders and delete this file.                             **
**                                                                         **
***************************************************************************** 
-->


<template>
  <v-form
    ref="myForm"
    v-on:submit.prevent="checkFeatures"
    :disabled="saving"
  >
    <v-card>
      <v-card-title class="text-center justify-center py-6">
        <div class="text-h6 text-sm-h4">
          {{ language.t("RoadProfiler.Milling.title") }}
        </div>
        <v-spacer></v-spacer>
        <v-btn
          icon
          color="blue darken-1"
          @click="$emit('close')"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-alert
        v-if="error"
        dense
        type="warning"
        dismissible
      >
        {{ error }}
      </v-alert>

      <v-card-text v-if="success">
        <v-alert
          dense
          type="success"
        >
          {{ language.t("RoadProfiler.Current.saved") }}
        </v-alert>
      </v-card-text>
      <v-card-actions v-if="success">
        <v-btn v-on:click="clearForm()">
          <v-icon>mdi-folder-plus-outline</v-icon>
          {{ language.t("RoadProfiler.Current.createNewTask") }}
        </v-btn>
        <v-btn v-on:click="openTask()">
          <v-icon>mdi-map-search-outline</v-icon>
          {{ language.t("RoadProfiler.Current.viewtask") }}
        </v-btn>
      </v-card-actions>

      <div v-show="!success">
        <v-container>
          <v-row dense>
            <v-col>
              <v-card width="100%">
                <v-card-text>
                  <v-text-field
                    v-model="form.name"
                    ref="name"
                    :placeholder="language.t('RoadProfiler.Milling.taskName')"
                    maxlength="45"
                  >
                  </v-text-field>
                  <v-text-field
                    v-model="form.description"
                    ref="description"
                    :placeholder="language.t('RoadProfiler.Milling.description')"
                  ></v-text-field>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col>
              <v-card style="min-width: 250px">
                <v-card-title>
                  <div class="text-h6 text-sm-h5">
                    {{ language.t("RoadProfiler.Milling.millingModel") }}
                  </div>
                </v-card-title>
                <v-card-text>
                  <DxfInput
                    ref="millingmodel"
                    :dxffile.sync="form.millingmodel.file"
                    :layername.sync="form.millingmodel.layername"
                    :numfeatures.sync="form.millingmodel.numfeatures"
                  />
                </v-card-text>
              </v-card>
            </v-col>
            <v-col>
              <v-card style="min-width: 250px">
                <v-card-title>
                  <div class="text-h6 text-sm-h5">
                    {{ language.t("RoadProfiler.Milling.designModel") }}
                  </div>
                </v-card-title>
                <v-card-text>
                  <DxfInput
                    ref="designmodel"
                    :dxffile.sync="form.designmodel.file"
                    :layername.sync="form.designmodel.layername"
                    :numfeatures.sync="form.designmodel.numfeatures"
                  />
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col>
              <v-card width="100%">
                <v-card-title>
                  <div class="text-h6 text-sm-h5">
                    {{ language.t("RoadProfiler.Milling.environmentModel") }}
                  </div>
                </v-card-title>
                <v-card-text>
                  <DxfInput
                    ref="envmodel"
                    :dxffile.sync="form.envmodel.file"
                    :layername.sync="form.envmodel.layername"
                    :numfeatures.sync="form.envmodel.numfeatures"
                  />
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col>
              <v-card width="100%">
                <v-card-title>
                  <div class="text-h6 text-sm-h5">Projectie</div>
                </v-card-title>
                <v-card-text>
                  <ProjectionSelect v-model="form.projection" />
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col>
              <v-card width="100%">
                <v-card-title>
                  <div class="text-h6 text-sm-h5">Project</div>
                </v-card-title>
                <v-card-text>
                  <v-container class="pa-0">
                    <v-row no-gutters>
                      <v-col :cols="6">
                        <v-select
                          class="py-1 px-2"
                          v-model="form.selectedProject"
                          :items="projectList"
                          dense
                          :loading="projectListLoading"
                          :disabled="projectListLoading"
                        >
                        </v-select>
                      </v-col>
                      <v-col :cols="6">
                        <v-text-field
                          v-if="form.selectedProject == 0"
                          v-model="form.newProjectName"
                          placeholder="Project naam"
                          maxlength="45"
                          dense
                        >
                        </v-text-field>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>

        <v-card-actions class="card-actions">
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            x-large
            v-on:click="closeDialog()"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-btn
            color="success"
            x-large
            type="submit"
            :disabled="!complete || saving"
          >
            <v-icon>mdi-check</v-icon>
          </v-btn>
        </v-card-actions>
      </div>
    </v-card>
    <v-dialog
      v-model="featureWarning"
      max-width="500px"
    >
      <v-card class="text-center">
        <v-card-title>
          <v-icon
            class="mr-2"
            x-large
            color="orange"
          >
            mdi-alert-circle-outline
          </v-icon>
          {{ language.t("RoadProfiler.Current.tooManyFeaturesAlertTitle") }}
        </v-card-title>

        <v-card-text>
          {{
            language.t("RoadProfiler.Current.tooManyFeaturesAlertDescription")
          }}
        </v-card-text>

        <v-card-actions>
          <v-btn
            color="primary"
            text
            @click="featureWarning = false"
          >
            {{ language.t("App.cancel") }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :disabled="!complete || saving"
            text
            @click="createTask()"
          >
            {{ language.t("App.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-form>
</template>

<script>
import ProjectionSelect from "../formfields/ProjectionSelect";
import DxfInput from "../formfields/DxfInput";

export default {
  components: {
    ProjectionSelect,
    DxfInput,
  },
  props: {},
  data() {
    return {
      featureWarning: false,
      complete: false,
      saving: false,
      error: null,
      success: false,
      task: null,
      maxRecommendedFeatures: 100000,

      projectList: [
        { text: "Geen project", value: -1 },
        { text: "Nieuw project", value: 0 },
        { divider: true },
        { header: "projecten" },
      ],
      projectListLoading: true,

      form: {
        name: "",
        description: "",
        designmodel: {
          file: null,
          layername: "",
          numfeatures: 0,
        },
        millingmodel: {
          file: null,
          layername: "",
          numfeatures: 0,
        },
        envmodel: {
          file: null,
          layername: "",
          numfeatures: 0,
        },
        projection: "EPSG:28992",
        selectedProject: -1,
        newProjectName: "",
      },
    };
  },
  mounted: function () {
    this.getProjects();
  },
  computed: {},
  watch: {
    form: {
      /* validate if the form data is complete */
      handler(val) {
        if (
          val.name != "" &&
          val.projection != "" &&
          val.millingmodel.file &&
          val.designmodel.file
        )
          this.complete = true;
        else this.complete = false;
      },
      deep: true,
    },
  },
  methods: {
    getProjects() {
      this.projectListLoading = true;
      var _this = this;
      this.$axios.get(route("api.projects")).then((res) => {
        for (let i = 0; i < res.data.length; i++) {
          if (res.data[i].active == "1")
            _this.projectList.push({
              value: res.data[i].projectId,
              text: res.data[i].name,
              isProject: true,
            });
        }
        _this.projectListLoading = false;
      });
    },
    closeDialog() {
      this.$emit("close");
    },
    checkFeatures() {
      if (
        this.form.designmodel.numfeatures >= this.maxRecommendedFeatures ||
        this.form.millingmodel.numfeatures >= this.maxRecommendedFeatures ||
        this.form.envmodel.numfeatures >= this.maxRecommendedFeatures
      )
        this.featureWarning = true;
      else this.createtask();
    },
    createtask() {
      var _this = this;
      this.saving = true;
      this.error = null;
      let data = new FormData();
      data.set("name", this.form.name);
      data.set("description", this.form.description);
      data.set("dm_file", this.form.designmodel.file);
      data.set("dm_layername", this.form.designmodel.layername);
      data.set("dm_numfeatures", this.form.designmodel.numfeatures);
      data.set("mm_file", this.form.millingmodel.file);
      data.set("mm_layername", this.form.millingmodel.layername);
      data.set("mm_numfeatures", this.form.millingmodel.numfeatures);
      data.set("env_file", this.form.envmodel.file);
      data.set("env_layername", this.form.envmodel.layername);
      data.set("env_numfeatures", this.form.envmodel.numfeatures);
      data.set("projection", this.form.projection);
      data.set(
        "project",
        this.form.selectedProject == -1 ? "" : this.form.selectedProject
      );
      if (this.form.selectedProject == 0)
        data.set("newproject", this.form.newProjectName);

      this.$axios
        .post(route("api.tasks.rpr"), data, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((res) => {
          _this.$emit("taskCreated");
          _this.task = res.data;
          _this.success = true;
          _this.saving = false;
        })
        .catch((error) => {
          _this.saving = false;
          console.log(error.response.data);
          if (error.response.data.error)
            _this.error = error.response.data.error;
          else _this.error = "unknown server error";
        });
    },
    openTask() {
      this.$emit("openTask", this.task.taskId);
      this.$emit("close");
    },
    clearForm() {
      this.$refs.millingmodel.reset();
      this.$refs.designmodel.reset();
      this.$refs.envmodel.reset();
      this.form.file = null;
      this.form.name = "";
      this.form.description = "";
      this.form.designmodel.file = null;
      this.form.designmodel.layername = null;
      this.form.designmodel.numfeatures = 0;
      this.form.millingmodel.file = null;
      this.form.millingmodel.layername = null;
      this.form.millingmodel.numfeatures = 0;
      this.form.envmodel.file = null;
      this.form.envmodel.layername = null;
      this.form.envmodel.numfeatures = 0;
      this.form.projection = "EPSG:28992";
      this.form.selectedProject = -1;
      this.form.newProjectName = "";
      this.error = null;
      this.success = false;
    },
  },
};
</script>

<style scoped>
.v-card__subtitle,
.v-card__text,
.v-card__title {
  padding-left: 16px;
  padding-top: 8px;
  padding-bottom: 0px;
}
</style>